import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Home from "../pages/home/Home";
import Login from "../pages/auth/Login";
import useAffiliateParam from "../hooks/useAffiliateParam";
import AppLoader from "../components/UI/AppLoader";

// Le reste du catalogue de pages est découpé : sans cela, tout le site part
// dans un seul bundle que le visiteur télécharge avant de voir l'accueil.
// Accueil et connexion restent en statique — ce sont les deux entrées réelles.
const Register = lazy(() => import("../pages/auth/Register"));
const ProduitDetails = lazy(() => import("../pages/produitDetails/ProduitDetails"));
const Commandes = lazy(() => import("../pages/commandes/Commandes"));
const Messages = lazy(() => import("../pages/messages/Messages"));
const Historique = lazy(() => import("../pages/commandes/Historique"));
const MonArgent = lazy(() => import("../pages/money/MonArgent"));
const Account = lazy(() => import("../pages/comptes/Account"));
const Informations = lazy(() => import("../pages/comptes/Informations"));
const About = lazy(() => import("../pages/appPages/About"));
const Services = lazy(() => import("../pages/appPages/Services"));
const Restore = lazy(() => import("../pages/auth/Restore"));
const ResetPasswordConfirm = lazy(() => import("../pages/auth/ResetPasswordConfirm"));
const Politique = lazy(() => import("../pages/appPages/Politique"));
const Conditions = lazy(() => import("../pages/appPages/Conditions"));
const Promises = lazy(() => import("../pages/appPages/Promises"));
const EchantillonDetails = lazy(() => import("../pages/echantillonDetails/EchantillonDetails"));
const ListEchantillon = lazy(() => import("../pages/listEchantillon/listEchange"));
const PaymentConfirmation = lazy(() => import("../pages/payment-confirmation/PaymentConfirmation"));
const Affiliation = lazy(() => import("../pages/appPages/Affiliation"));
const GuideImportation = lazy(() => import("../pages/appPages/GuideImportation"));
const PolitiqueReception = lazy(() => import("../pages/appPages/PolitiqueReception"));
const Retour = lazy(() => import("../pages/appPages/Retour"));
const Business = lazy(() => import("../pages/appPages/Business"));
const BonsPlans = lazy(() => import("../pages/bonsPlans/BonsPlans"));
const Paiement = lazy(() => import("../pages/appPages/Paiement"));
const LivraisonPolitique = lazy(() => import("../pages/appPages/LivraisonPolitique"));
const Electronique = lazy(() => import("../pages/categories/Electronique"));
const Femme = lazy(() => import("../pages/categories/Femme"));
const Beaute = lazy(() => import("../pages/categories/Beaute"));
const Homme = lazy(() => import("../pages/categories/Homme"));
const ShareLinkResolver = lazy(() => import("../pages/shareLink/ShareLinkResolver"));
const CategoryPage = lazy(() => import("../pages/categories/CategoryPage"));
const VariationsPage = lazy(() => import("../pages/variations/VariationsPage"));
const OrderDetail = lazy(() => import("../pages/commandes/OrderDetail"));
const Cart = lazy(() => import("../pages/cart/Cart"));

const AnimatedRoutes = () => {
  const location = useLocation();

  // Le jeton d'affiliation peut arriver par l'URL, posé par l'aperçu de lien
  // rendu côté serveur ; il est stocké puis retiré de la barre d'adresse.
  useAffiliateParam();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<AppLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home/>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path="/register" element={<Register/>}/>
          <Route path="/produit-details/:id" element={<ProduitDetails/>}/>
          <Route path="/variations/:id" element={<VariationsPage/>}/>
          <Route path="/commandes" element={<Commandes/>}/>
          <Route path="/messages" element={<Messages/>}/>
          <Route path="/historique" element={<Historique/>}/>
          <Route path="/money" element={<MonArgent/>}/>
          <Route path="/account" element={<Account/>}/>
          <Route path="/informations" element={<Informations/>}/>
          <Route path="/about" element={<About/>}/>
          <Route path="/services" element={<Services/>}/>
          <Route path="/conditions" element={<Conditions/>}/>
          <Route path="/politique" element={<Politique/>}/>
          <Route path="/restore" element={<Restore/>}/>
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordConfirm/>}/>
          <Route path="/promises" element={<Promises/>}/>
          <Route path="/cart" element={<Cart/>}/>
          <Route path="/echantillonDetails/:id" element={<EchantillonDetails />} />
          <Route path="/echantillonDetails" element={<EchantillonDetails />} />
          <Route path="/bons-plans" element={<BonsPlans />} />
          <Route path="/listEchantillon" element={<ListEchantillon />} />
          <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
          <Route path="/affiliation" element={<Affiliation/>}/>
          <Route path="/guide-importation" element={<GuideImportation/>}/>
          <Route path="/politique-reception" element={<PolitiqueReception/>}/>
          <Route path="/retour" element={<Retour/>}/>
          <Route path="/business" element={<Business/>}/>
          <Route path="/paiement" element={<Paiement/>}/>
          <Route path="/livraison-politique" element={<LivraisonPolitique/>}/>
          <Route path="/electronique" element={<Electronique/>}/>
          <Route path="/femme" element={<Femme/>}/>
          <Route path="/beaute" element={<Beaute/>}/>
          <Route path="/homme" element={<Homme/>}/>
          <Route path="/categorie/:slug" element={<CategoryPage/>}/>
          <Route path="/commande/:id" element={<OrderDetail/>}/>
          <Route path="/s/:identifier" element={<ShareLinkResolver/>}/>
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

/**
 * `children` est rendu **dans** le routeur : les compagnons de l'application —
 * onboarding, bienvenue, invitation à installer — vivent hors des routes mais
 * emploient `Link` et `useLocation`. Montés à côté d'`AppRoutes`, ils étaient
 * hors du `Router` : le premier `Link` levait, et l'écran devenait blanc.
 */
const AppRoutes = ({ children }) => {
    return (
        <Router>
            <AnimatedRoutes />
            {children}
        </Router>
    );
}

export default AppRoutes;
